import { useState, useEffect, Fragment, } from "react";
import backgroundImage from '../../images/contact-us.webp';
import { getUserSession } from "../../Common/lib/Session";

import useDocumentTitle from "../../Common/useDocumentTitle";
import { Dialog, Transition } from '@headlessui/react';
import { SiFacebook, SiX, SiInstagram } from "react-icons/si";
import { HiOutlineCheckCircle } from 'react-icons/hi';
import { ImSpinner8 } from "react-icons/im";
// import ReCAPTCHA from 'react-google-recaptcha'; //must have react-google-recaptcha installed for recaptcha v2

import Header from "../../Common/header";
import Footer from "../../Common/footer";
import { sendContactEmail } from "../../Common/email/sendEmail";

/**
 * Returns the Technical Support / Contact Us page component.
 * 
 * @returns The Technical Support / Contact Us page component.
*/
export default function SupportPage() {
  const [errors, setErrors] = useState({
    fullNameError: "",
    emailError: "",
    subjectError: "",
    messageError: "",
    policyError: "",
    // captchaError: "",
  });
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
    privacyPolicy: false,
  });
  const [failError, setFailError] = useState("");
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  // const [captcha, setCaptcha] = useState("");

  useDocumentTitle("Contact Us");

  useEffect(() => {
    getUserInfo();
    initReCaptcha(); // Initialize recaptcha v3 when the component mounts
  
    return () => {
      // Clean up the recaptcha v3 script and badge when the component is unmounted
      const existingScript = document.querySelector("script[src^='https://www.google.com/recaptcha/api.js']");
      if (existingScript) {
        existingScript.remove();
      }
  
      const badgeElement = document.querySelector(".grecaptcha-badge");
      if (badgeElement) {
        badgeElement.remove()
      }
    };
  }, []);

  /**
   * Retrives info of the currently logged-in user to be used for name and email autofill.
   */
  async function getUserInfo() {
    let user = await getUserSession();
    if (user.fname !== undefined) {
      setFormData((prevState) => ({
        ...prevState,
        fullName: `${user.fname} ${user.lname}`,
        email: user.email,
        subject: "",
        message: "",
      }))
    }
  }

  /**
   * Initializes the recaptcha v3 script when loading the componenent.
   */
  const initReCaptcha = () => {
    const existingScript = document.querySelector("script[src^='https://www.google.com/recaptcha/api.js']");
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=6LdviS8nAAAAADTMbv8PyimFwdXXvQLGjkfm1FIE";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  };

  /**
   * Handles changing the captcha value - used for recaptcha V2
   */
  // const onCaptchaChange = (value) => {
  //   setCaptcha(value)
  //   setErrors(prevState => ({
  //     ...prevState,
  //     captchaError: "",
  //   }));
  // }

  /**
   * Handles the input changes for all the input fields on the contact page.
   * @param {*} event 
   */
  const handleChange = (event, name) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: event.target.value,
    }));
  };

  /**
   * Clears errors and sends an email to info@arcticresearchfoundation.ca.
   * @param {*} event 
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormDisabled(true);
    setLoading(true);
    setFailError("");
    setErrors({
      fullNameError: "",
      emailError: "",
      subjectError: "",
      messageError: "",
      policyError: "",
    });

    let isValid = validateFields();

    if (isValid) {
      try {
        let captchaToken = '';
        if (typeof grecaptcha !== 'undefined' && grecaptcha.execute) {
          captchaToken = await grecaptcha.execute("6LdviS8nAAAAADTMbv8PyimFwdXXvQLGjkfm1FIE", {action: 'contact'});
        }

        await sendContactEmail(
          formData.fullName,
          formData.email,
          formData.subject,
          formData.message,
          captchaToken
        );

        setOpenSuccessModal(true);
        setLoading(false);

        setTimeout(() => {
          setOpenSuccessModal(false);
          window.location.reload();
        }, 2000);
      } catch (err) {
        setFailError(err.response?.data?.error || "Failed to send email. Please try again.");
        setFormDisabled(false);
        setLoading(false);
      }
    } else {
      setFormDisabled(false);
      setLoading(false);
    }
  };

  /**
   * Validates form input fields. Checks if any fields are empty.
   * @returns Boolean - True if all input fieldes are valid. False if invalid.
   */
  function validateFields() {
    let result = true;

    // Check if any of the fields are empty
    if (formData.fullName.trim() === "") {
      setErrors(prevState => ({
        ...prevState,
        fullNameError: "Required",
      }));
      result = false;
    }
    if (formData.email.trim() === "") {
      setErrors(prevState => ({
        ...prevState,
        emailError: "Required",
      }));
      result = false;
    }
    if (formData.subject.trim() === "") {
      setErrors(prevState => ({
        ...prevState,
        subjectError: "Required",
      }));
      result = false;
    }
    if (formData.message.trim() === "") {
      setErrors(prevState => ({
        ...prevState,
        messageError: "Required",
      }));
      result = false;
    }

    // Check subject and message length
    if (formData.subject.length > 200) {
      setErrors(prevState => ({
        ...prevState,
        subjectError: "Provided subject is too long",
      }));
      result = false;
    }
    if (formData.message.length > 4000) {
      setErrors(prevState => ({
        ...prevState,
        messageError: "Provided message is too long",
      }));
      result = false;
    }

    const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!EMAIL_REGEXP.test(formData.email)) {
      setErrors(prevState => ({
        ...prevState,
        emailError: "Invalid Email",
      }));
      result = false;
    }

    // Check if privacy policy checkbox was checked off
    if (!formData.privacyPolicy) {
      setErrors(prevState => ({
        ...prevState,
        policyError: "Required",
      }));
      result = false;
    }

    //check if recaptcha v2 has been completed.
    // if(!captcha){
    //   setErrors(prevState => ({
    //     ...prevState,
    //     captchaError: "Required",
    //   }));
    //   result = false;
    // }

    return result;
  }

  // Joins class names. Used for adding red borders to inputs with errors.
  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  return (
    <>
      <Header />

      <main className="bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-6 lg:mx-auto grid gap-x-0 lg:gap-x-8 gap-y-6 lg:gap-y-8 grid-cols-1 lg:grid-cols-5 py-8 lg:py-14">

          {/* from-purple-400 to-green-700 */}
          {/* from-white to-black */}
          <div className="bg-gradient-to-tr from-white to-black col-span-2 relative isolate overflow-hidden rounded-xl shadow-sm">
            <img
              src={backgroundImage}
              alt="Arctic plains landscape"
              className="absolute inset-0 -z-10 h-full w-full object-cover bg-blend-overlay opacity-80"
            />

            <div className="p-8 mb-0 lg:mb-80">
              <div className="relative">
                <h1 className="text-2xl text-right font-bold tracking-tight text-white sm:text-4xl">
                  Contact Us
                </h1>
                <div className="text-right">
                  <h2 className="mt-10 text-white font-semibold text-lg">General and Media Inquiries</h2>
                  <a href="mailto:info@arcticresearchfoundation.ca" className="text-white hover:underline">info@arcticresearchfoundation.ca</a>

                  <h2 className="mt-10 text-white font-semibold text-lg">Arctic Research Foundation</h2>
                  <p className="text-white">67 Erb St W, Waterloo,</p>
                  <p className="text-white">ON N2L 6C2</p>

                  <h2 className="mt-10 text-white font-semibold text-lg">Social Media</h2>
                  <div className="mt-2 justify-end flex space-x-6 items-center">
                    <a
                      target="_blank"
                      href=" https://www.facebook.com/arcticfocus/"
                      className="text-white hover:text-gray-100"
                    >
                      <span className="sr-only">Facebook</span>

                      <SiFacebook className="h-6 w-6" />
                    </a>

                    <a
                      target="_blank"
                      className="text-white hover:text-gray-100"
                      href="https://www.instagram.com/arcticfocus/?hl=en"
                    >
                      <span className="sr-only">Instagram</span>

                      <SiInstagram className="h-6 w-6" />
                    </a>

                    <a
                      target="_blank"
                      className="text-white hover:text-gray-100"
                      href="https://twitter.com/arcticfocus?lang=en"
                    >
                      <span className="sr-only">Twitter</span>

                      <SiX className="h-6 w-6" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-3 bg-gray-100 p-8 border border-gray-200 shadow-sm rounded-xl dark:bg-gray-800 dark:border-gray-700">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
              <div className="col-span-2">
                <label htmlFor="fullName" className="flex text-sm font-medium text-gray-700 dark:text-white">
                  Full Name
                  {errors.fullNameError.length > 0 &&
                    <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
                      &nbsp;- {errors.fullNameError}
                    </p>
                  }
                </label>

                <div className="mt-1">
                  <input
                    required
                    value={formData.fullName}
                    type="text"
                    id="fullName"
                    name="fullName"
                    onChange={(e) => handleChange(e, "fullName")}
                    autoComplete="on"
                    className="py-3 px-4 border outline-none focus:ring-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="col-span-2">
                <label htmlFor="email" className="flex text-sm font-medium text-gray-700 dark:text-white">
                  Email
                  {errors.emailError.length > 0 &&
                    <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
                      &nbsp;- {errors.emailError}
                    </p>
                  }
                </label>

                <div className="mt-1">
                  <input
                    required
                    value={formData.email}
                    id="email"
                    name="email"
                    type="email"
                    onChange={(e) => handleChange(e, "email")}
                    className="py-3 px-4 border outline-none focus:ring-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="col-span-2">
                <label htmlFor="subject" className="flex text-sm font-medium text-gray-700 dark:text-white">
                  Subject
                  {errors.subjectError.length > 0 &&
                    <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
                      &nbsp;- {errors.subjectError}
                    </p>
                  }
                </label>

                <div className="mt-1">
                  <input
                    required
                    type="text"
                    id="subject"
                    name="subject"
                    onChange={(e) => handleChange(e, "subject")}
                    value={formData.subject}
                    className="py-3 px-4 border outline-none focus:ring-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="col-span-2">
                <label htmlFor="message" className="flex text-sm font-medium text-gray-700 dark:text-white">
                  Message
                  {errors.messageError.length > 0 &&
                    <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
                      &nbsp;- {errors.messageError}
                    </p>
                  }
                </label>

                <div className="mt-1">
                  <textarea
                    rows={6}
                    required
                    id="message"
                    name="message"
                    onChange={(e) => handleChange(e, "message")}
                    value={formData.message}
                    className="max-h-96 py-3 px-4 outline-none focus:ring-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="text-sm sm:text-base col-span-2 mb-6">
                <div className="flex justify-between items-center">
                    <label htmlFor="privacyPolicy" className="font-medium text-gray-700 text-sm flex flex-wrap items-center dark:text-white">
                      <input
                        id="privacyPolicy"
                        onChange={() => { setFormData((prevState) => ({ ...prevState, privacyPolicy: !formData.privacyPolicy })) }}
                        checked={formData.privacyPolicy}
                        type="checkbox"
                        className={classNames(errors.policyError.length > 0 ? 'border-red-600 dark:border-red-600' : 'border-gray-400 dark:border-gray-600', 'mr-2 h-5 w-5 rounded text-blue-700 focus:ring-blue-700 dark:bg-gray-700')}
                      />
                      I understand and agree to the&nbsp;<a onClick={() => window.open("/privacy-policy")} className="font-medium text-blue-700 underline dark:text-blue-500">Privacy Policy</a>
                    </label>
                  {/* <div className="flex-shrink flex justify-end" >
                    <div className="w-64 mr-1" style={{ transform: "scale(0.75)" }}>
                      <div className={errors.captchaError.length > 0 ? "captcha-error" : ""}>
                        <ReCAPTCHA sitekey="6LfIkBsnAAAAAIzrdwRprW68I4kYgSUPYyI1enuA" onChange={onCaptchaChange}  />
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>

              <div className="sm:col-span-2">
                <button
                  disabled={formDisabled}
                  type="submit"
                  className="w-full gap-x-2 inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Send Message
                  {loading &&
                    <ImSpinner8 className="w-5 h-5 text-gray-50 animate-spin dark:text-gray-600 " />
                  }
                </button>
              </div>
              {failError.length > 0 &&
                <p className="ml-2 italic block font-base font-normal text-red-600 text-center col-span-2">
                  {failError}
                </p>
              }
            </form>
          </div>
        </div>
      </main>

      <Transition.Root show={openSuccessModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpenSuccessModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div>
                    <span className="flex justify-center">
                      <HiOutlineCheckCircle className="w-32 h-32 mx-auto text-green-500" />
                    </span>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="sm:text-xl text-lg font-semibold leading-6 text-gray-900">
                        Email Sent Successfully
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">

                        </p>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Footer />
    </>
  );
};